/** API register/createProfile format: 919876543210 (country code + digits, no + or hyphen) */
export function formatPhoneForRegister(countryCode, mobile) {
  const cc = String(countryCode ?? '91').replace(/\D/g, '') || '91';
  const digits = String(mobile ?? '').replace(/\D/g, '');
  return `${cc}${digits}`;
}

/**
 * Same digit string as login `username` and register `phone_number`: no + prefix.
 * If the user already pasted a full international number (e.g. 919876543210 with CC 91), do not prefix twice.
 */
export function formatPhoneDigitsForAuthLookup(countryCode, mobile) {
  const cc = String(countryCode ?? '91').replace(/\D/g, '') || '91';
  const digits = String(mobile ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith(cc) && digits.length >= cc.length + 6) {
    return digits;
  }
  return `${cc}${digits}`;
}

/** sendOTP path format: +919876543210 (no hyphen) */
export function formatPhoneE164(countryCode, mobile) {
  const cc = String(countryCode ?? '91').replace(/\D/g, '') || '91';
  const digits = String(mobile ?? '').replace(/\D/g, '');
  return `+${cc}${digits}`;
}

/** Digits only, e.g. guardian mobile as 919999999999 */
export function stripPhoneDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

export const DEFAULT_COUNTRY_CODE = '91';

/**
 * Expected **local** mobile length (without country dial code) for supported countries.
 * Matches the country-code dropdowns used in login / signup / forgot-password.
 */
export const LOCAL_MOBILE_LENGTH_BY_COUNTRY = {
  91: 10, // India
  1: 10, // US / Canada
  44: 10, // UK mobile (without leading 0)
  61: 9, // Australia mobile (without leading 0)
  65: 8, // Singapore
  971: 9, // UAE
};

export function normalizeCountryCode(countryCode) {
  return String(countryCode ?? DEFAULT_COUNTRY_CODE).replace(/\D/g, '') || DEFAULT_COUNTRY_CODE;
}

/** Local digit count expected for a country dial code. Defaults to 10. */
export function getLocalMobileLength(countryCode) {
  const cc = normalizeCountryCode(countryCode);
  return LOCAL_MOBILE_LENGTH_BY_COUNTRY[cc] ?? 10;
}

/**
 * Human-readable mobile for UI: `+91 9876543210` (space between country code and local).
 * Accepts API-style digits (`919876543210`), E.164 (`+919876543210`), or local-only (`9876543210`).
 */
export function formatPhoneForDisplay(value, { defaultCountryCode = DEFAULT_COUNTRY_CODE } = {}) {
  const digits = stripPhoneDigits(value);
  if (!digits) return '';

  const codes = Object.keys(LOCAL_MOBILE_LENGTH_BY_COUNTRY).sort(
    (a, b) => String(b).length - String(a).length,
  );
  for (const cc of codes) {
    const localLen = LOCAL_MOBILE_LENGTH_BY_COUNTRY[cc];
    if (digits.startsWith(String(cc)) && digits.length === String(cc).length + localLen) {
      return `+${cc} ${digits.slice(String(cc).length)}`;
    }
  }

  const cc = normalizeCountryCode(defaultCountryCode);
  const localLen = getLocalMobileLength(cc);
  if (digits.length === localLen) {
    return `+${cc} ${digits}`;
  }
  if (digits.startsWith(cc) && digits.length > cc.length) {
    return `+${cc} ${digits.slice(cc.length)}`;
  }
  return digits;
}

/**
 * Keep digits only and cap to the local length for the selected country.
 * Use for inputs that sit next to a country-code selector.
 */
export function sanitizeLocalMobileInput(value, countryCode = DEFAULT_COUNTRY_CODE) {
  const max = getLocalMobileLength(countryCode);
  return stripPhoneDigits(value).slice(0, max);
}

/**
 * Digits-only sanitizer for free-form phone fields (optional max length).
 * Default max covers longest supported CC + local (e.g. 971 + 9).
 */
export function sanitizePhoneDigitsInput(value, maxLen = 15) {
  return stripPhoneDigits(value).slice(0, Math.max(1, Number(maxLen) || 15));
}

/**
 * Validate a **local** mobile number (no country code in the field).
 * @returns {{ ok: true, digits: string } | { ok: false, message: string }}
 */
export function validateLocalMobile(mobile, countryCode = DEFAULT_COUNTRY_CODE) {
  const expected = getLocalMobileLength(countryCode);
  const digits = stripPhoneDigits(mobile);
  if (!digits) {
    return { ok: false, message: 'Please enter a valid mobile number.' };
  }
  if (digits.length !== expected) {
    return {
      ok: false,
      message: `Enter a valid ${expected}-digit mobile number.`,
    };
  }
  return { ok: true, digits };
}

/**
 * Validate a phone stored as digits only — either local (e.g. 9876543210) or
 * full international (e.g. 919876543210). Used where there is no CC dropdown.
 *
 * @param {string} value
 * @param {{ defaultCountryCode?: string, optional?: boolean }} [options]
 * @returns {{ ok: true, digits: string } | { ok: false, message: string }}
 */
export function validatePhoneDigitsValue(
  value,
  { defaultCountryCode = DEFAULT_COUNTRY_CODE, optional = false } = {},
) {
  const digits = stripPhoneDigits(value);
  if (!digits) {
    if (optional) return { ok: true, digits: '' };
    return { ok: false, message: 'Please enter a valid mobile number.' };
  }

  const cc = normalizeCountryCode(defaultCountryCode);
  const localLen = getLocalMobileLength(cc);

  if (digits.length === localLen) {
    return { ok: true, digits };
  }
  if (digits.startsWith(cc) && digits.length === cc.length + localLen) {
    return { ok: true, digits };
  }

  for (const [otherCc, len] of Object.entries(LOCAL_MOBILE_LENGTH_BY_COUNTRY)) {
    if (digits.startsWith(String(otherCc)) && digits.length === String(otherCc).length + len) {
      return { ok: true, digits };
    }
  }

  return {
    ok: false,
    message: `Enter a valid ${localLen}-digit mobile number (or include country code).`,
  };
}
